import React from 'react';

import { Breakpoints, CTA, ThemeProvider, useIsMobile } from '@vw-marketing/us-components';

import { getUniqueExternalSubscriptions } from '../../../../shared/utils/general';
import { SiriusXMSubscription, Vehicles } from '../../../../shared/typings/main';
import { StyledButtonsContainer } from './styles';
import { FeatureAppTrackingManager } from '../../../../shared/typings/tagging';
import { onVWBasicButtonClickWithProcessEvent } from '../../../../shared/tagging/tagging-helpers';
import RemoveVehiclePrimaryRole from '../remove-vehicle-primary-role';
import RemoveVehicleAdditionalRole from '../remove-vehicle-additional-role';

export interface RemoveVehicleFormViewProps {
  translationHandler: (key: string) => string;
  trackingManager: FeatureAppTrackingManager;
  userVehicles: Vehicles[];
  userRole?: string;
  isCanada?: boolean;
  goToConfirmation: () => void;
  onGoBack: () => void;
}

export const RemoveVehicleFormView: React.FC<RemoveVehicleFormViewProps> = ({
  translationHandler,
  trackingManager,
  userVehicles,
  userRole,
  isCanada = false,
  goToConfirmation,
  onGoBack,
}) => {
  const isDesktop: boolean = !useIsMobile(Breakpoints.b1920);

  const handleGoBackAction = () => {
    try {
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Vehicle Management',
        StepName: 'Vehicle Details - Remove Vehicle',
        StepNumber: '5.2',
      }, {
        name: 'Go Back',
      });
    } catch (error) {
      console.warn('Error tracking go back button click:', error);
    }
    onGoBack();
  };

  const handleContinueAction = () => {
    try {
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Vehicle Management',
        StepName: 'Vehicle Details - Remove Vehicle',
        StepNumber: '5.2',
      }, {
        name: 'Continue',
      });
    } catch (error) {
      console.warn('Error tracking continue button click:', error);
    }
    goToConfirmation();
  };

  const externalSubscriptions = isCanada ? [] : getUniqueExternalSubscriptions(userVehicles);

  const hasSiriusXMSubscription = isCanada ? undefined : userVehicles.find(
    (vehicle: Vehicles) =>
      vehicle.siriusXMSubscription?.audioSubscription != null,
  )?.siriusXMSubscription as SiriusXMSubscription | undefined;

  if (!userRole) return null;

  const deactivateButtons = (): React.ReactElement => (
    <StyledButtonsContainer>
      <CTA
        tag="button"
        stretchContent={!isDesktop}
        emphasis="secondary"
        onClick={handleGoBackAction}
      >
        {translationHandler('vehicleManagement.removeVehicle.confirmation.button.goBack')}
      </CTA>
      <CTA
        tag="button"
        stretchContent={!isDesktop}
        onClick={handleContinueAction}
      >
        {translationHandler('accountSettings.deactivate.myvwaccount.continue.label')}
      </CTA>
    </StyledButtonsContainer>
  );

  return (
    <ThemeProvider theme="main">
      {userRole === 'primary' ? (
        <div data-testid="remove-vehicle-form">
          <RemoveVehiclePrimaryRole
            translationHandler={translationHandler}
            deactivateButtons={deactivateButtons}
            externalSubscriptions={externalSubscriptions}
            hasSiriusXMSubscription={hasSiriusXMSubscription}
            userVehicles={userVehicles}
          />
        </div>
      ) : (
        <div data-testid="remove-vehicle-form">
          <RemoveVehicleAdditionalRole
            translationHandler={translationHandler}
            deactivateButtons={deactivateButtons}
            externalSubscriptions={externalSubscriptions}
            hasSiriusXMSubscription={hasSiriusXMSubscription}
            userVehicles={userVehicles}
          />
        </div>
      )}
    </ThemeProvider>
  )
};
